import { Box, CircularProgress } from '@material-ui/core';
import { Skeleton } from '@material-ui/lab';
import { formatDateReadable, formatDateTimeReadable, getLenderById, LenderById, Loan } from '@nemuru-eng/js-api-client';
import { useEffect, useState } from 'react';
import ReactJson from 'react-json-view';

import { useLoanApplication } from '@/components/shared/components/ConditionsSummary/LoanConditionsSummary/components/LenderApplications/components/LoanApplication/hooks/useLoanApplication';
import { LoanApplicationCard } from '@/components/shared/components/ConditionsSummary/LoanConditionsSummary/components/LenderApplications/components/LoanApplicationCard';
import { LoanApplicationHeader } from '@/components/shared/components/ConditionsSummary/LoanConditionsSummary/components/LenderApplications/components/LoanApplicationHeader/LoanApplicationHeader';
import { RiskScoring } from '@/components/shared/components/ConditionsSummary/LoanConditionsSummary/components/LenderApplications/components/RiskScoring/RiskScoring';
import { LoanApplicationMode } from '@/components/shared/components/ConditionsSummary/LoanConditionsSummary/components/LenderApplications/types/LoanApplicationMode';
import { Font } from '@/components/shared/components/Font';
import { NotificationSnackbar } from '@/components/shared/components/NotificationSnackbar/NotificationSnackbar';
import { ReadOnlyDataRow } from '@/components/shared/components/ReadOnlyDataRow';
import { StandardButton } from '@/components/shared/components/StandardButton';
import { StyledTooltip } from '@/components/shared/components/StyledTooltip';
import { Text } from '@/components/shared/components/Text';
import { useDialog } from '@/components/shared/hooks/useDialog';
import { useTranslation } from '@/components/shared/hooks/useTranslation';
import { PageHighlightTypeName } from '@/config/types';
import { useConfigContext } from '@/contexts/ConfigContext/ConfigContext';
import { usePageContext } from '@/contexts/PageContext/PageContext';
import { useCustomTheme } from '@/hooks/useCustomTheme';

export const LENDER_ERROR_HASH_ID = 'lender-application-error';

interface LoanApplicationProps {
  loan: Loan;
  loanApplicationId: string;
  processingEnabled: boolean;
  mode: LoanApplicationMode;
}

export const LoanApplication = ({
  loan,
  loanApplicationId,
  processingEnabled,
  mode = LoanApplicationMode.VIEW_ONLY,
}: LoanApplicationProps) => {
  const {
    state: { darkMode },
  } = useConfigContext();

  const {
    actions,
    state: { loanHighlights },
  } = usePageContext();

  const { DialogComponent, setOpened } = useDialog();
  const { getTextIdTranslation } = useTranslation();
  const { customBorderRadius } = useCustomTheme();

  const {
    loanApplication,
    processing,
    error,
    success,
    closeAlerts,
    loadingProcessResult,
    processLoanApplication,
    processResult,
    getProcessResponse,
  } = useLoanApplication({ loanApplicationId, loanId: loan.id });

  const [currentLender, setCurrentLender] = useState<LenderById | undefined>(undefined);

  const isSelected = loanApplication?.lenderFields.loanApplicationStatus.state === 'selected';
  const lenderName = loanApplication?.lenderName;
  const lenderId = loanApplication?.lenderId;
  const lenderApprovalStatus = loanApplication?.lenderFields.loanApplicationStatus.lenderApprovalStatus;
  const approvalStatusTranslation = getTextIdTranslation(lenderApprovalStatus as string);
  const hasError = isSelected && !lenderApprovalStatus;

  useEffect(() => {
    const incidenceReported = loanHighlights.some((file) => file.hashId === LENDER_ERROR_HASH_ID);
    if (hasError && !incidenceReported) setHighlight();
  }, [hasError]);

  const getAsyncLenderAndSetState = async (id) => {
    const currentLender = await getLenderById({ id });
    setCurrentLender(currentLender);
  };

  useEffect(() => {
    if (lenderId) {
      getAsyncLenderAndSetState(lenderId);
    }
  }, [lenderId]);

  const openDialog = () => {
    if (loanApplication) {
      setOpened(true);
    }
    if (!currentLender?.policyId) getProcessResponse();
  };

  const setHighlight = () => {
    actions.addLoanHighlights([
      {
        textId: 'LOAN_APPLICATION_ERROR',
        type: PageHighlightTypeName.ERROR,
        hashId: LENDER_ERROR_HASH_ID,
      },
    ]);
  };

  const cardTooltip = (
    <>
      {isSelected && (
        <Box mb={2}>
          <strong>
            <Text tId={'SELECTED_LENDER'} />
          </strong>
        </Box>
      )}
      <Box>
        {lenderApprovalStatus ? (
          <>
            <Text
              tId={'LENDER_X_ANSWERED_X_TO_APPLICATION'}
              dynamicDataTexts={[lenderName || '', `${approvalStatusTranslation}`]}
            />
          </>
        ) : (
          <Text tId={'NO_RESPONSE_FROM_LENDER'} />
        )}
      </Box>
    </>
  );

  if (!loan) {
    return null;
  }

  return (
    <>
      <StyledTooltip title={cardTooltip} tooltipProps={{ enterDelay: 500 }}>
        <Box>
          {currentLender ? (
            <LoanApplicationCard
              onClick={openDialog}
              lenderApprovalStatus={lenderApprovalStatus}
              isSelected={isSelected}
              lenderName={currentLender.name}
              mode={mode}
            />
          ) : (
            <Skeleton
              variant="rect"
              width={'100%'}
              height={'68px'}
              style={{ borderRadius: customBorderRadius.paper, marginBottom: 16 }}
            />
          )}
        </Box>
      </StyledTooltip>
      {loanApplication && (
        <>
          <DialogComponent
            buttons={
              processingEnabled && (
                <StyledTooltip title={<Text tId={'PROCESS_TOOLTIP'} />} tooltipProps={{ enterDelay: 500 }}>
                  <Box>
                    <StandardButton main disabled={processing} onClick={processLoanApplication}>
                      <Text tId={'PROCESS'} />
                      {processing && (
                        <>
                          <Box ml={2} />
                          <CircularProgress size={14} thickness={4} color={'inherit'} />
                        </>
                      )}
                    </StandardButton>
                  </Box>
                </StyledTooltip>
              )
            }
          >
            <Box width={'100%'} minWidth={300} mt={2} mb={5}>
              {lenderName && (
                <LoanApplicationHeader
                  loan={loan}
                  loanApplicationId={loanApplicationId}
                  lenderApprovalStatus={lenderApprovalStatus}
                  isSelected={isSelected}
                  lenderName={lenderName}
                  mode={mode}
                />
              )}
            </Box>
            <ReadOnlyDataRow
              labelTextId={'LENDER_REQUEST_ID'}
              value={loanApplication?.lenderFields.lenderRequestId}
              forceSingleLine
              copyButton
              underlined
            />
            <ReadOnlyDataRow
              labelTextId={'LENDER_STATE'}
              value={
                <Text
                  tId={
                    loanApplication?.lenderFields.loanApplicationStatus.state
                      ? `LOAN_APPLICATION_STATUS_${loanApplication?.lenderFields.loanApplicationStatus.state}`
                      : ''
                  }
                />
              }
              underlined
            />
            <ReadOnlyDataRow
              labelTextId={'SENT_AT'}
              value={
                loanApplication.loanLenderBorrowerContract.sentDate
                  ? formatDateReadable(loanApplication.loanLenderBorrowerContract.sentDate)
                  : undefined
              }
              underlined
            />
            <ReadOnlyDataRow
              labelTextId={'SIGNED_AT'}
              value={
                loanApplication.loanLenderBorrowerContract.signedDate
                  ? formatDateReadable(loanApplication.loanLenderBorrowerContract.signedDate)
                  : undefined
              }
              underlined
            />
            <ReadOnlyDataRow
              labelTextId={'LAST_UPDATED_AT'}
              value={formatDateTimeReadable(loanApplication.updatedAt)}
              underlined
            />
            {currentLender?.policyId ? (
              <RiskScoring loan={loan} />
            ) : (
              <Box pt={3}>
                <Font variant={'subtitle2'} color={'textSecondary'}>
                  <Text tId={'RESPONSE'} />:
                </Font>
                <Box mt={2} minHeight={200}>
                  {loadingProcessResult ? (
                    <CircularProgress size={20} color={'secondary'} />
                  ) : processResult ? (
                    <ReactJson
                      src={processResult}
                      theme={darkMode ? 'monokai' : 'rjv-default'}
                      name={null}
                      iconStyle="square"
                      //@ts-ignore
                      enableAdd={false}
                      enableEdit={false}
                      enableDelete={false}
                      enableClipboard={false}
                      displayObjectSize={false}
                      displayDataTypes={false}
                      style={{
                        minHeight: 200,
                        overflowX: 'auto',
                        overflowY: 'clip',
                        whiteSpace: 'nowrap',
                        lineHeight: '12px',
                      }}
                    />
                  ) : (
                    <Font variant={'body2'}>
                      <Text tId={'FILE_NOT_FOUND'} />
                    </Font>
                  )}
                </Box>
              </Box>
            )}
            <Box mt={3} />
            <NotificationSnackbar
              open={!!error || success}
              onClose={closeAlerts}
              message={<Text tId={'PROCESSING_SUCCESSFUL'} />}
              subMessage={
                success ? (
                  <Text tId={'PROCESSING_DID_UPDATE_THE_APPLICATION_SUCCESSFULLY'} />
                ) : (
                  <Text tId={'PROCESSING_DID_NOT_ALTER_APPLICATION'} />
                )
              }
              level={success ? 'success' : 'info'}
              style={{ position: 'relative', width: '100%', bottom: '-20px' }}
            />
          </DialogComponent>
        </>
      )}
    </>
  );
};
